import styled from '@emotion/styled';
import React, {RefObject} from 'react';
import {useTranslation} from 'react-i18next';

import {Close, Search} from '@ELib/assets/images';
import {mixinSVGIcon, mixinUnstyledButton} from '@ELib/styles/mixins';
import {keyboardShortcutSearchHandler, useKeyboard} from 'webapp-lib/utils';

import {ETextInput, ETextInputProps} from './ETextInput';

export type ESearchInputProps = {
  autoFocus?: boolean;
  onChange: (value: string) => void;
  onClear?: () => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  value: string | undefined;
  inputRef?: RefObject<HTMLInputElement>;
  name?: string;
} & Pick<ETextInputProps, 'onFocus' | 'onKeydown' | 'endAdornment'>;

export const ESearchInput = ({
  onChange,
  onFocus,
  onClear,
  onSubmit,
  onKeydown,
  placeholder,
  autoFocus = true,
  value,
  inputRef,
  name,
  endAdornment,
}: ESearchInputProps): React.ReactElement => {
  const {t} = useTranslation('lib');

  const internalOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const reset = () => {
    onChange('');
    onClear?.();
    inputRef?.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    keyboardShortcutSearchHandler(e, () => {
      inputRef?.current?.focus();
    });
  };

  useKeyboard(handleKeyDown);

  const handleInputKeydown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      onSubmit?.(value ?? '');
    }

    onKeydown?.(e);
  };

  return (
    <ETextInput
      ariaLabel={t('actions.search')}
      autoFocus={autoFocus}
      endAdornment={
        <>
          <AdornmentWrapper onClick={reset}>
            {value && value.length > 0 ? <Close /> : <Search />}
          </AdornmentWrapper>
          {endAdornment}
        </>
      }
      inputRef={inputRef}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={internalOnChange}
      onFocus={onFocus}
      onKeydown={handleInputKeydown}
    />
  );
};

const AdornmentWrapper = styled.button`
  ${mixinUnstyledButton}
  cursor: pointer;
  width: var(--search-input-adornment-dimension);
  height: var(--search-input-adornment-dimension);
  svg {
    ${mixinSVGIcon}
    margin: var(--gutter-tiny);
  }
`;
